import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import ProtectedRoute from './ProtectedRoute';
import Login from './Login';
import Register from './Register';
import { 
  Plus, Settings, Eye, Edit3, Save, X, ExternalLink, Mic, Globe, 
  FileText, MessageSquare, Users, BarChart3, Clock, Trash2, 
  Copy, Download, Upload, AlertCircle, CheckCircle, Loader2,
  Search, Filter, MoreVertical, Play, Pause, Volume2, VolumeX,
  DollarSign
} from 'lucide-react';
import { api } from './services/api';
import TokenUsageDashboard from './TokenUsageDashboard';

// TypeScript interfaces
interface Website {
  id: string;
  name: string;
  domain: string;
  industry: string;
  timezone: string;
  status: string;
  sessions: number;
  lastUpdated: string;
  avgResponseTime: string;
  satisfactionRate: number;
  totalConversations: number;
  prompt: string;
  personality: string;
  sources: string[];
  conversationStyle: string;
  language: string;
  responseLength: string;
  fallbackBehavior: string;
  notificationEmail?: string;
  uiTemplate?: string;
  // AGENT MODE SETTINGS
  agentMode?: string;  // 'standard', 'openai_realtime', or 'gemini_realtime'
  
  // Standard Pipeline (when agentMode = 'standard')
  sttProvider?: string | null;    // Add | null
  sttModel?: string | null;       // Add | null
  llmProvider?: string | null;    // Add | null
  llmModel?: string | null;       // Add | null
  ttsProvider?: string | null;    // Add | null
  ttsModel?: string | null;       // Add | null
  ttsVoice?: string | null;       // Add | null
  
  // Realtime Pipeline (when agentMode = 'openai_realtime' or 'gemini_realtime')
  realtimeModel?: string | null;  // Add | null
  realtimeVoice?: string | null;  // Add | null
  
  voiceSettings: {
    model: string;
    speed: string;
    interruption: boolean;
    autoStart: boolean;
  };
  businessHours: {
    enabled: boolean;
    timezone: string;
    hours: any;
  };
  integrations: {
    crm: string;
    analytics: string;
    helpdesk: string;
  };
}

interface TabButtonProps {
  tabId: string;
  icon: React.ComponentType<{ size: number }>;
  label: string;
  active: boolean;
}

interface DeleteModalProps {
  website: Website;
  onConfirm: (websiteId: string) => void;
  onCancel: () => void;
}

// Main Dashboard Component
const VoiceBotDashboard: React.FC = () => {
  const [websites, setWebsites] = useState<Website[]>([]);
  const [selectedWebsite, setSelectedWebsite] = useState<Website | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<Website | null>(null);
  const [showTokenUsage, setShowTokenUsage] = useState(false);
  const { user, logout } = useAuth();  // ADD THIS LINE

  // Sample data - replace with API calls --> replaced with real API calls
useEffect(() => {
  const loadWebsites = async () => {
    const websiteList = await api.getWebsites();
    
    // Get real session counts for each website
    const websitesWithSessionCounts = await Promise.all(
      websiteList.map(async (website: any) => {
        const sessionCount = await api.getWebsiteSessionCount(website.id);
        console.log(`${website.name}: Real sessions = ${sessionCount}`); // Debug log
        console.log(`${website.name} (${website.id}): Real sessions = ${sessionCount}`);
        return {
          ...website,
          sessions: sessionCount  // Replace hardcoded value with real count
        };
      })
    );
    
    setWebsites(websitesWithSessionCounts);
  };
  loadWebsites();
}, []);

  const [currentConfig, setCurrentConfig] = useState<Website>({
    id: '',
    name: '',
    domain: '',
    industry: '',
    timezone: '',
    prompt: '',
    personality: '',
    sources: [],
    conversationStyle: 'professional',
    language: 'en',
    notificationEmail: '',
    uiTemplate: 'default',
    agentMode: 'standard',

    // Standard defaults
    sttProvider: 'deepgram',
    sttModel: 'nova-3',
    llmProvider: 'google',
    llmModel: 'gemini-2.0-flash',
    ttsProvider: 'cartesia',
    ttsModel: 'sonic',
    ttsVoice: 'default',

    // Realtime defaults
    realtimeModel: 'gpt-realtime-mini',
    realtimeVoice: 'shimmer',
    
    responseLength: 'medium',
    fallbackBehavior: 'transfer',
    status: 'active',
    sessions: 0,
    lastUpdated: '',
    avgResponseTime: '0s',
    satisfactionRate: 0,
    totalConversations: 0,
    voiceSettings: {
      model: 'professional-female',
      speed: 'normal',
      interruption: true,
      autoStart: false
    },
    businessHours: {
      enabled: false,
      timezone: 'UTC',
      hours: {}
    },
    integrations: {
      crm: '',
      analytics: '',
      helpdesk: ''
    }
  });

  // // Add this useEffect to reset pipeline fields when agentMode changes
  // useEffect(() => {
  //   if (!currentConfig.agentMode) return;
    
  //   if (currentConfig.agentMode === 'openai_realtime') {
  //     // Clear standard pipeline fields
  //     setCurrentConfig(prev => ({
  //       ...prev,
  //       sttProvider: null,
  //       sttModel: null,
  //       llmProvider: null,
  //       llmModel: null,
  //       ttsProvider: null,
  //       ttsModel: null,
  //       ttsVoice: null,
  //       // ALWAYS set OpenAI defaults (removed the || check)
  //       realtimeModel: 'gpt-realtime-mini',        // ✅ Force set
  //       realtimeVoice: 'shimmer'                    // ✅ Force set
  //     }));
  //   } else if (currentConfig.agentMode === 'gemini_realtime') {
  //     // Clear standard pipeline fields
  //     setCurrentConfig(prev => ({
  //       ...prev,
  //       sttProvider: null,
  //       sttModel: null,
  //       llmProvider: null,
  //       llmModel: null,
  //       ttsProvider: null,
  //       ttsModel: null,
  //       ttsVoice: null,
  //       // ALWAYS set Gemini defaults (removed the || check)
  //       realtimeModel: 'gemini-2.5-flash-exp',     // ✅ Force set
  //       realtimeVoice: 'Puck'                       // ✅ Force set
  //     }));
  //   } else if (currentConfig.agentMode === 'standard') {
  //     // Clear realtime fields
  //     setCurrentConfig(prev => ({
  //       ...prev,
  //       realtimeModel: null,
  //       realtimeVoice: null,
  //       // Set Standard defaults if not already set
  //       sttProvider: prev.sttProvider || 'deepgram',
  //       sttModel: prev.sttModel || 'nova-3',
  //       llmProvider: prev.llmProvider || 'google',
  //       llmModel: prev.llmModel || 'gemini-2.0-flash',
  //       ttsProvider: prev.ttsProvider || 'cartesia',
  //       ttsModel: prev.ttsModel || 'sonic-3',
  //       ttsVoice: prev.ttsVoice || 'f31cc6a7-c1e8-4764-980c-60a361443dd1'
  //     }));
  //   }
  // }, [currentConfig.agentMode]); // Only trigger when agentMode changes

  // NEW: Add this provider-change useEffect
  useEffect(() => {
    if (currentConfig.agentMode !== 'standard') return;
    
    // Reset LLM model when provider changes
    if (currentConfig.llmProvider === 'google' && currentConfig.llmModel && !currentConfig.llmModel.includes('gemini')) {
      setCurrentConfig(prev => ({...prev, llmModel: 'gemini-2.0-flash'}));
    } else if (currentConfig.llmProvider === 'openai' && currentConfig.llmModel && !currentConfig.llmModel.includes('gpt')) {
      setCurrentConfig(prev => ({...prev, llmModel: 'gpt-4o-mini'}));
    } else if (currentConfig.llmProvider === 'anthropic' && currentConfig.llmModel && !currentConfig.llmModel.includes('claude')) {
      setCurrentConfig(prev => ({...prev, llmModel: 'claude-3-sonnet'}));
    }
  }, [currentConfig.llmProvider, currentConfig.sttProvider, currentConfig.ttsProvider]);


  // Force update realtime fields when switching between realtime modes
  useEffect(() => {
    if (currentConfig.agentMode === 'openai_realtime') {
      // Only update if values are actually wrong for OpenAI mode
      if (currentConfig.realtimeModel === 'gemini-2.5-flash-exp' || 
          currentConfig.realtimeVoice === 'Puck') {
        setCurrentConfig(prev => ({
          ...prev,
          realtimeModel: 'gpt-realtime-mini',
          realtimeVoice: 'shimmer'
        }));
      }
    } else if (currentConfig.agentMode === 'gemini_realtime') {
      // Only update if values are actually wrong for Gemini mode
      if (currentConfig.realtimeModel === 'gpt-realtime-mini' || 
          currentConfig.realtimeVoice === 'shimmer') {
        setCurrentConfig(prev => ({
          ...prev,
          realtimeModel: 'gemini-2.5-flash-native-audio-preview-12-2025',
          realtimeVoice: 'Puck'
        }));
      }
    }
  }, [currentConfig.agentMode, currentConfig.realtimeModel, currentConfig.realtimeVoice]);

  // Show Token Usage Dashboard
  if (showTokenUsage) {
    return <TokenUsageDashboard onBack={() => setShowTokenUsage(false)} />;
  }

  const industries = [
    'E-commerce', 'Healthcare', 'Finance', 'Education', 'Technology',
    'Customer Support', 'Real Estate', 'Travel', 'Hospitality', 'Other'
  ];

  const conversationStyles = [
    { value: 'professional', label: 'Professional & Formal' },
    { value: 'friendly', label: 'Friendly & Casual' },
    { value: 'technical', label: 'Technical & Detailed' },
    { value: 'empathetic', label: 'Empathetic & Supportive' },
    { value: 'sales', label: 'Sales-Oriented' }
  ];

  const voiceModels = [
    { 
      value: 'professional-female', 
      label: 'Professional Female',
      description: 'Neutral, clear, and polished - ideal for business'
    },
    { 
      value: 'professional-male', 
      label: 'Professional Male',
      description: 'Warm, authoritative, and confident'
    },
    { 
      value: 'friendly-female', 
      label: 'Friendly Female',
      description: 'Bright, energetic, and approachable'
    },
    { 
      value: 'friendly-male', 
      label: 'Friendly Male',
      description: 'Warm, casual, and conversational'
    },
    { 
      value: 'technical-male', 
      label: 'Technical Male',
      description: 'Deep, calm, and knowledgeable'
    }
  ];

  const timezones = [
    // UTC
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    
    // North America
    { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
    { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
    { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
    { value: 'America/Anchorage', label: 'Alaska Time' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
    { value: 'America/Toronto', label: 'Toronto' },
    { value: 'America/Vancouver', label: 'Vancouver' },
    { value: 'America/Mexico_City', label: 'Mexico City' },
    
    // Europe
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
    { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)' },
    { value: 'Europe/Rome', label: 'Rome (CET/CEST)' },
    { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
    { value: 'Europe/Brussels', label: 'Brussels (CET/CEST)' },
    { value: 'Europe/Vienna', label: 'Vienna (CET/CEST)' },
    { value: 'Europe/Stockholm', label: 'Stockholm (CET/CEST)' },
    { value: 'Europe/Moscow', label: 'Moscow (MSK)' },
    { value: 'Europe/Istanbul', label: 'Istanbul (TRT)' },
    
    // Asia
    { value: 'Asia/Dubai', label: 'Dubai (GST)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Asia/Colombo', label: 'Sri Lanka (IST)' },
    { value: 'Asia/Dhaka', label: 'Bangladesh (BST)' },
    { value: 'Asia/Bangkok', label: 'Bangkok (ICT)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
    { value: 'Asia/Shanghai', label: 'China (CST)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Seoul', label: 'Seoul (KST)' },
    { value: 'Asia/Manila', label: 'Manila (PHT)' },
    { value: 'Asia/Jakarta', label: 'Jakarta (WIB)' },
    
    // Australia & Pacific
    { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
    { value: 'Australia/Melbourne', label: 'Melbourne (AEDT/AEST)' },
    { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
    { value: 'Australia/Perth', label: 'Perth (AWST)' },
    { value: 'Pacific/Auckland', label: 'Auckland (NZDT/NZST)' },
    
    // South America
    { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
    { value: 'America/Buenos_Aires', label: 'Buenos Aires (ART)' },
    { value: 'America/Santiago', label: 'Santiago (CLT)' },
    { value: 'America/Bogota', label: 'Bogotá (COT)' },
    
    // Africa
    { value: 'Africa/Cairo', label: 'Cairo (EET)' },
    { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
    { value: 'Africa/Lagos', label: 'Lagos (WAT)' },
    { value: 'Africa/Nairobi', label: 'Nairobi (EAT)' },
  ];

  // Filter websites based on search and status
  const filteredWebsites = websites.filter(website => {
    const matchesSearch = website.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         website.domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         website.industry.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || website.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleCreateNew = () => {
    setCurrentConfig({
      id: `web_${Date.now()}`,
      name: '',
      domain: '',
      industry: '',
      timezone: '',
      prompt: '',
      personality: '',
      sources: [],
      conversationStyle: 'professional',
      language: 'en',
      responseLength: 'medium',
      fallbackBehavior: 'transfer',
      status: 'active',
      sessions: 0,
      lastUpdated: '',
      avgResponseTime: '0s',
      satisfactionRate: 0,
      totalConversations: 0,
      voiceSettings: {
        model: 'professional-female',
        speed: 'normal',
        interruption: true,
        autoStart: false
      },
      businessHours: {
        enabled: false,
        timezone: 'UTC',
        hours: {}
      },
      integrations: {
        crm: '',
        analytics: '',
        helpdesk: ''
      }
    });
    setSelectedWebsite(null);
    setIsEditing(true);
    setActiveTab('basic');
  };

  const handleEdit = (website: Website) => {
    setCurrentConfig({ ...website });
    setSelectedWebsite(website);
    setIsEditing(true);
    setActiveTab('basic');
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      if (selectedWebsite) {
        // Update existing website
        const updatedWebsite = await api.updateWebsite(selectedWebsite.id, currentConfig);
        setWebsites(websites.map(w => w.id === selectedWebsite.id ? updatedWebsite : w));
      } else {
        // Create new website
        const newWebsite = await api.createWebsite(currentConfig);
        setWebsites([...websites, newWebsite]);
      }
      setIsEditing(false);
      setSelectedWebsite(null);
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save website configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (websiteId: string) => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      setWebsites(websites.filter(w => w.id !== websiteId));
      setShowDeleteModal(null);
    } catch (error) {
      console.error('Delete error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusToggle = async (websiteId: string) => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      setWebsites(websites.map(w => 
        w.id === websiteId 
          ? { ...w, status: w.status === 'active' ? 'paused' : 'active' }
          : w
      ));
    } catch (error) {
      console.error('Status toggle error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateEmbedCode = (websiteId: string) => {
    return `<script>
  window.VoiceBotConfig = {
    websiteId: '${websiteId}',
    apiEndpoint: 'https://wvoiceagent.kersaisystems.com/'
  };
</script>
<script src="https://wvoiceagent.kersaisystems.com/voice-bot-widget.js?v=110"></script>`;
  };

  const copyEmbedCode = (websiteId: string) => {
    navigator.clipboard.writeText(generateEmbedCode(websiteId));
    // You could add a toast notification here
  };

  const TabButton: React.FC<TabButtonProps> = ({ tabId, icon: Icon, label, active }) => (
    <button
      onClick={() => setActiveTab(tabId)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
        active ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'hover:bg-gray-100'
      }`}
    >
      <Icon size={18} />
      {label}
    </button>
  );

  // Delete Modal Component
  const DeleteModal: React.FC<DeleteModalProps> = ({ website, onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="text-red-500" size={24} />
          <h3 className="text-lg font-semibold">Delete Website Configuration</h3>
        </div>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete the configuration for "{website.name}"? 
          This action cannot be undone and will stop the voice bot on this website.
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(website.id)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  if (isEditing) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border">
            <div className="flex items-center justify-between p-6 border-b">
              <h1 className="text-2xl font-bold">
                {selectedWebsite ? 'Edit Website Configuration' : 'Add New Website'}
              </h1>
              <div className="flex gap-3">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg flex items-center gap-2"
                  disabled={isLoading}
                >
                  <X size={18} />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isLoading || !currentConfig.name || !currentConfig.domain || !currentConfig.timezone}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:bg-gray-400"
                >
                  {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                  {isLoading ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </div>

            <div className="flex">
              <div className="w-64 border-r p-6">
                <nav className="space-y-2">
                  <TabButton tabId="basic" icon={Globe} label="Basic Settings" active={activeTab === 'basic'} />
                  <TabButton tabId="prompt" icon={MessageSquare} label="AI Configuration" active={activeTab === 'prompt'} />
                  <TabButton tabId="sources" icon={FileText} label="Knowledge Sources" active={activeTab === 'sources'} />
                  <TabButton tabId="voice" icon={Mic} label="Voice Settings" active={activeTab === 'voice'} />
                  <TabButton tabId="advanced" icon={Settings} label="Advanced" active={activeTab === 'advanced'} />
                  <TabButton tabId="embed" icon={Copy} label="Embed Code" active={activeTab === 'embed'} />
                </nav>
              </div>

              <div className="flex-1 p-6">
                {activeTab === 'basic' && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-semibold">Basic Settings</h2>
                    
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium mb-2">Website Name *</label>
                        <input
                          type="text"
                          value={currentConfig.name}
                          onChange={(e) => setCurrentConfig({...currentConfig, name: e.target.value})}
                          placeholder="My E-commerce Store"
                          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Domain *</label>
                        <input
                          type="text"
                          value={currentConfig.domain}
                          onChange={(e) => setCurrentConfig({...currentConfig, domain: e.target.value})}
                          placeholder="shop.example.com"
                          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Industry</label>
                        <select
                          value={currentConfig.industry}
                          onChange={(e) => setCurrentConfig({...currentConfig, industry: e.target.value})}
                          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select Industry</option>
                          {industries.map(industry => (
                            <option key={industry} value={industry}>{industry}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Language</label>
                        <select
                          value={currentConfig.language}
                          onChange={(e) => setCurrentConfig({...currentConfig, language: e.target.value})}
                          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="en">English</option>
                          <option value="es">Spanish</option>
                          <option value="fr">French</option>
                          <option value="de">German</option>
                          <option value="pt">Portuguese</option>
                          <option value="it">Italian</option>
                          <option value="si">Sinhala</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Timezone *</label>
                        <select
                          value={currentConfig.timezone}
                          onChange={(e) => setCurrentConfig({...currentConfig, timezone: e.target.value})}
                          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select Timezone</option>
                          {timezones.map(tz => (
                            <option key={tz.value} value={tz.value}>{tz.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Agent Mode Selector */}
                      <div className="col-span-2">
                        <label className="block text-sm font-medium mb-2">Agent Mode</label>
                        <select
                          value={currentConfig.agentMode || 'standard'}
                          onChange={(e) => setCurrentConfig({...currentConfig, agentMode: e.target.value})}
                          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                        {/* <select
                          value={currentConfig.agentMode || 'standard'}
                          onChange={(e) => {
                            const newMode = e.target.value;
                            if (newMode === 'openai_realtime') {
                              setCurrentConfig({
                                ...currentConfig,
                                agentMode: newMode,
                                sttProvider: null, sttModel: null,
                                llmProvider: null, llmModel: null,
                                ttsProvider: null, ttsModel: null, ttsVoice: null,
                                realtimeModel: 'gpt-realtime-mini',
                                realtimeVoice: 'shimmer'
                              });
                            } else if (newMode === 'gemini_realtime') {
                              setCurrentConfig({
                                ...currentConfig,
                                agentMode: newMode,
                                sttProvider: null, sttModel: null,
                                llmProvider: null, llmModel: null,
                                ttsProvider: null, ttsModel: null, ttsVoice: null,
                                realtimeModel: 'gemini-2.5-flash-native-audio-preview-12-2025',
                                realtimeVoice: 'Puck'
                              });
                            } else {
                              setCurrentConfig({
                                ...currentConfig,
                                agentMode: newMode,
                                realtimeModel: null,
                                realtimeVoice: null,
                                // Set Standard defaults
                                sttProvider: currentConfig.sttProvider || 'deepgram',
                                sttModel: currentConfig.sttModel || 'nova-3',
                                llmProvider: currentConfig.llmProvider || 'google',
                                llmModel: currentConfig.llmModel || 'gemini-2.0-flash',
                                ttsProvider: currentConfig.ttsProvider || 'cartesia',
                                ttsModel: currentConfig.ttsModel || 'sonic-3',
                                ttsVoice: currentConfig.ttsVoice || 'f31cc6a7-c1e8-4764-980c-60a361443dd1'
                              });
                            }
                          }}
                          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        > */}
                          <option value="standard">Standard Pipeline</option>
                          <option value="openai_realtime">Realtime (OpenAI)</option>
                          <option value="gemini_realtime">Realtime (Gemini)</option>
                        </select>
                        <p className="text-sm text-gray-500 mt-1">
                          {currentConfig.agentMode === 'standard' 
                            ? 'Choose separate providers for STT, LLM, and TTS'
                            : 'Single model handles everything (OpenAI/Gemini only)'}
                        </p>
                      </div>

                      {/* Conditional: Show for STANDARD Mode */}
                      {currentConfig.agentMode === 'standard' && (
                        <>
                          {/* STT Provider */}
                          <div className="col-span-2">
                            <label className="block text-sm font-medium mb-2">Speech-to-Text (STT) Provider</label>
                            <select
                              value={currentConfig.sttProvider || 'deepgram'}
                              onChange={(e) => setCurrentConfig({
                                ...currentConfig, 
                                sttProvider: e.target.value
                              })}
                              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="deepgram">Deepgram</option>
                              <option value="assemblyai">AssemblyAI</option>
                              <option value="whisper">OpenAI Whisper</option>
                            </select>
                          </div>
                          
                          {/* STT Model */}
                          <div className="col-span-2">
                            <label className="block text-sm font-medium mb-2">STT Model</label>
                            <select
                              value={currentConfig.sttModel || 'nova-2'}
                              onChange={(e) => setCurrentConfig({
                                ...currentConfig, 
                                sttModel: e.target.value
                              })}
                              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="nova-3">nova-3</option>
                              <option value="nova-2">nova-2</option>
                              <option value="flux">flux</option>
                            </select>
                          </div>

                          {/* LLM Provider */}
                          <div className="col-span-2">
                            <label className="block text-sm font-medium mb-2">LLM Provider</label>
                            <select
                              value={currentConfig.llmProvider || 'google'}
                              onChange={(e) => setCurrentConfig({
                                ...currentConfig, 
                                llmProvider: e.target.value
                              })}
                              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="google">Google Gemini</option>
                              <option value="openai">OpenAI GPT</option>
                              <option value="anthropic">Anthropic Claude</option>
                            </select>
                          </div>
                          
                          {/* LLM Model */}
                          <div className="col-span-2">
                            <label className="block text-sm font-medium mb-2">LLM Model</label>
                            <select
                              value={currentConfig.llmModel || 'gemini-2.0-flash'}
                              onChange={(e) => setCurrentConfig({
                                ...currentConfig, 
                                llmModel: e.target.value
                              })}
                              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              {currentConfig.llmProvider === 'google' ? (
                                <>
                                  <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
                                  <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
                                  <option value="gemini-1.5-flash">Gemini 1.5 Flash</option>
                                </>
                              ) : currentConfig.llmProvider === 'openai' ? (
                                <>
                                  <option value="gpt-4o-mini">gpt-4o-mini</option>
                                  <option value="gpt-4.1-mini">gpt-4.1-mini</option>
                                  <option value="gpt-4.1-nano">gpt-4.1-nano</option>
                                  <option value="gpt-5-mini">gpt-5-mini</option>
                                </>
                              ) : (
                                <>
                                  <option value="claude-3-opus">Claude 3 Opus</option>
                                  <option value="claude-3-sonnet">Claude 3 Sonnet</option>
                                </>
                              )}
                            </select>
                          </div>

                          {/* TTS Provider */}
                          <div className="col-span-2">
                            <label className="block text-sm font-medium mb-2">Text-to-Speech (TTS) Provider</label>
                            <select
                              value={currentConfig.ttsProvider || 'cartesia'}
                              onChange={(e) => setCurrentConfig({
                                ...currentConfig, 
                                ttsProvider: e.target.value
                              })}
                              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="cartesia">Cartesia</option>
                              <option value="elevenlabs">ElevenLabs</option>
                              <option value="openai">OpenAI TTS</option>
                            </select>
                          </div>
                          
                          {/* TTS Model */}
                          <div className="col-span-2">
                            <label className="block text-sm font-medium mb-2">TTS Model</label>
                            <select
                              value={currentConfig.ttsModel || 'sonic'}
                              onChange={(e) => setCurrentConfig({
                                ...currentConfig, 
                                ttsModel: e.target.value
                              })}
                              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="sonic-3">sonic-3</option>
                              <option value="sonic-turbo">sonic-turbo</option>
                              <option value="sonic-2">sonic-2</option>
                            </select>
                          </div>
                          
                          {/* TTS Voice */}
                          <div className="col-span-2">
                            <label className="block text-sm font-medium mb-2">TTS Voice</label>
                            <select
                              value={currentConfig.ttsVoice || 'default'}
                              onChange={(e) => setCurrentConfig({
                                ...currentConfig, 
                                ttsVoice: e.target.value
                              })}
                              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="f31cc6a7-c1e8-4764-980c-60a361443dd1">Australian Lady</option>
                              <option value="fb7d50908-b17c-442d-ad8d-810c63997ed9">Calm Lady</option>
                            </select>
                          </div>
                        </>
                      )}

                      {/* Conditional: Show for REALTIME Mode */}
                      {(currentConfig.agentMode === 'openai_realtime' || currentConfig.agentMode === 'gemini_realtime') && (
                        <>
                          {/* Model */}
                          <div className="col-span-2">
                            <label className="block text-sm font-medium mb-2">Model</label>
                            <select
                              value={currentConfig.realtimeModel || (currentConfig.agentMode === 'openai_realtime' ? 'gpt-realtime-mini' : 'gemini-2.5-flash-native-audio-preview-12-2025')}
                              onChange={(e) => setCurrentConfig({
                                ...currentConfig, 
                                realtimeModel: e.target.value
                              })}
                              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              {currentConfig.agentMode === 'openai_realtime' ? (
                                <>
                                  <option value="gpt-realtime">gpt-realtime</option>
                                  <option value="gpt-realtime-mini">gpt-realtime-mini</option>
                                  <option value="gpt-4o-realtime-preview">gpt-4o-realtime-preview</option>
                                  <option value="gpt-4o-mini-realtime-preview">gpt-4o-mini-realtime-preview</option>
                                </>
                              ) : (
                                <>
                                  <option value="gemini-2.5-flash-native-audio-preview-12-2025">gemini-2.5-flash-native-audio (Dec 2025) ⭐</option>
                                  <option value="gemini-2.5-flash-native-audio-preview-09-2025">gemini-2.5-flash-native-audio (Sep 2025)</option>
                                  <option value="gemini-3.1-flash-live-preview">gemini-3.1-flash-live-preview</option>
                                  <option value="gemini-2.5-flash-exp">gemini-2.5-flash-exp (Legacy)</option>
                                </>
                              )}
                            </select>
                          </div>

                          {/* Voice */}
                          <div className="col-span-2">
                            <label className="block text-sm font-medium mb-2">Voice</label>
                            <select
                              value={currentConfig.realtimeVoice || (currentConfig.agentMode === 'openai_realtime' ? 'shimmer' : 'Puck')}
                              onChange={(e) => setCurrentConfig({
                                ...currentConfig, 
                                realtimeVoice: e.target.value
                              })}
                              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              {currentConfig.agentMode === 'openai_realtime' ? (
                                <>
                                  <option value="alloy">Alloy</option>
                                  <option value="echo">Echo</option>
                                  <option value="shimmer">Shimmer</option>
                                  <option value="verse">Verse</option>
                                  <option value="sage">Sage</option>
                                </>
                              ) : (
                                <>
                                  <option value="Leda">Leda (Professional Female)</option>
                                  <option value="Zephyr">Zephyr (Friendly Female)</option>
                                  <option value="Orus">Orus (Technical Male)</option>
                                  <option value="Puck">Puck (Casual Male)</option>
                                  <option value="Charon">Charon (Professional Male)</option>
                                  <option value="Kore">Kore (Casual Female)</option>
                                  <option value="Fenrir">Fenrir (Friendly Male)</option>
                                  <option value="Aoede">Aoede</option>
                                </>
                              )}
                            </select>
                          </div>
                        </>
                      )}

                      <div>
                        <label className="block text-sm font-medium mb-2">Status</label>
                        <select
                          value={currentConfig.status}
                          onChange={(e) => setCurrentConfig({...currentConfig, status: e.target.value})}
                          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="active">Active</option>
                          <option value="paused">Paused</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Notification Email</label>
                        <input
                          type="email"
                          value={currentConfig.notificationEmail || ''}
                          onChange={(e) => setCurrentConfig({...currentConfig, notificationEmail: e.target.value})}
                          placeholder="admin@example.com"
                          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Email address to receive lead notifications and conversation alerts
                        </p>
                      </div>
                      
                      {/* UI Template */}
                      <div>
                        <label className="block text-sm font-medium mb-2">UI Template</label>
                        <input
                          type="text"
                          value={currentConfig.uiTemplate || 'default'}
                          onChange={(e) => setCurrentConfig({ ...currentConfig, uiTemplate: e.target.value })}
                          placeholder="default"
                          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Enter template name: default, hudson, minimal, etc.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'prompt' && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-semibold">AI Configuration</h2>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">System Prompt</label>
                      <textarea
                        value={currentConfig.prompt}
                        onChange={(e) => setCurrentConfig({...currentConfig, prompt: e.target.value})}
                        rows={8}
                        placeholder="You are a helpful AI assistant for [Website Name]. You should be professional, knowledgeable about [Industry] industry, and always try to help users achieve their goals..."
                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Define how the AI should behave and respond to users on this website.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Personality & Tone</label>
                      <textarea
                        value={currentConfig.personality}
                        onChange={(e) => setCurrentConfig({...currentConfig, personality: e.target.value})}
                        rows={3}
                        placeholder="Describe the personality traits and communication style..."
                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium mb-2">Conversation Style</label>
                        <select
                          value={currentConfig.conversationStyle}
                          onChange={(e) => setCurrentConfig({...currentConfig, conversationStyle: e.target.value})}
                          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          {conversationStyles.map(style => (
                            <option key={style.value} value={style.value}>{style.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Response Length</label>
                        <select
                          value={currentConfig.responseLength}
                          onChange={(e) => setCurrentConfig({...currentConfig, responseLength: e.target.value})}
                          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="short">Short & Concise</option>
                          <option value="medium">Medium Detail</option>
                          <option value="detailed">Detailed & Comprehensive</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'sources' && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-semibold">Knowledge Sources</h2>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Data Sources</label>
                      <p className="text-sm text-gray-500 mb-4">
                        Add knowledge sources as text or upload files. These will help the AI answer questions accurately.
                      </p>
                      
                      <div className="space-y-3">
                        {currentConfig.sources.map((source, index) => (
                          <div key={index} className="flex gap-2">
                            <textarea
                              value={source}
                              onChange={(e) => {
                                const newSources = [...currentConfig.sources];
                                newSources[index] = e.target.value;
                                setCurrentConfig({...currentConfig, sources: newSources});
                              }}
                              placeholder="Enter knowledge text directly or paste a URL to a document..."
                              rows={3}
                              className="flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                            />
                            <button
                              onClick={() => {
                                const newSources = currentConfig.sources.filter((_, i) => i !== index);
                                setCurrentConfig({...currentConfig, sources: newSources});
                              }}
                              className="p-3 text-red-500 hover:bg-red-50 rounded-lg self-start"
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ))}
                        
                        <button
                          onClick={() => setCurrentConfig({...currentConfig, sources: [...currentConfig.sources, '']})}
                          className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-300 hover:text-blue-500 flex items-center justify-center gap-2"
                        >
                          <Plus size={18} />
                          Add Knowledge Source (Text or URL)
                        </button>
                      </div>
                
                      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Tips for Knowledge Sources:</h4>
                        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                          <li>Paste text directly: FAQs, product info, company policies</li>
                          <li>Add URLs: Link to documentation, knowledge base articles</li>
                          <li>Be specific: More detailed information = better AI responses</li>
                          <li>Update regularly: Keep information current for accurate answers</li>
                        </ul>
                      </div>
                    </div>
                
                    <div>
                      <label className="block text-sm font-medium mb-2">Fallback Behavior</label>
                      <select
                        value={currentConfig.fallbackBehavior}
                        onChange={(e) => setCurrentConfig({...currentConfig, fallbackBehavior: e.target.value})}
                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="apologize">Apologize and ask for clarification</option>
                        <option value="transfer">Transfer to human agent</option>
                        <option value="search">Search additional sources</option>
                        <option value="general">Provide general helpful response</option>
                      </select>
                      <p className="text-sm text-gray-500 mt-1">
                        What should the bot do when it can't find an answer?
                      </p>
                    </div>
                  </div>
                )}

                {activeTab === 'voice' && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-semibold">Voice Settings</h2>
                    
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium mb-2">Voice Model</label>
                        <select 
                          value={currentConfig.voiceSettings.model}
                          onChange={(e) => setCurrentConfig({
                            ...currentConfig, 
                            voiceSettings: {...currentConfig.voiceSettings, model: e.target.value}
                          })}
                          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          {voiceModels.map(model => (
                            <option key={model.value} value={model.value}>{model.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Speaking Speed</label>
                        <select 
                          value={currentConfig.voiceSettings.speed}
                          onChange={(e) => setCurrentConfig({
                            ...currentConfig, 
                            voiceSettings: {...currentConfig.voiceSettings, speed: e.target.value}
                          })}
                          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="slow">Slow</option>
                          <option value="normal">Normal</option>
                          <option value="fast">Fast</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <label className="flex items-center gap-3">
                        <input 
                          type="checkbox" 
                          checked={currentConfig.voiceSettings.interruption}
                          onChange={(e) => setCurrentConfig({
                            ...currentConfig, 
                            voiceSettings: {...currentConfig.voiceSettings, interruption: e.target.checked}
                          })}
                          className="rounded" 
                        />
                        <div>
                          <span className="text-sm font-medium">Wait for user to start</span>
                          <p className="text-sm text-gray-500">Agent waits for user to speak first</p>
                        </div>
                      </label>

                      <label className="flex items-center gap-3">
                        <input 
                          type="checkbox" 
                          checked={currentConfig.voiceSettings.autoStart}
                          onChange={(e) => setCurrentConfig({
                            ...currentConfig, 
                            voiceSettings: {...currentConfig.voiceSettings, autoStart: e.target.checked}
                          })}
                          className="rounded" 
                        />
                        <div>
                          <span className="text-sm font-medium">Agent initiates conversation</span>
                          <p className="text-sm text-gray-500">Greets user immediately when widget opens</p>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {activeTab === 'advanced' && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-semibold">Advanced Settings</h2>
                    
                    <div>
                      <h3 className="font-semibold mb-3">Integrations</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">CRM Integration</label>
                          <select 
                            value={currentConfig.integrations.crm}
                            onChange={(e) => setCurrentConfig({
                              ...currentConfig, 
                              integrations: {...currentConfig.integrations, crm: e.target.value}
                            })}
                            className="w-full p-3 border rounded-lg"
                          >
                            <option value="">None</option>
                            <option value="salesforce">Salesforce</option>
                            <option value="hubspot">HubSpot</option>
                            <option value="pipedrive">Pipedrive</option>
                            <option value="zoho">Zoho CRM</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Analytics</label>
                          <select 
                            value={currentConfig.integrations.analytics}
                            onChange={(e) => setCurrentConfig({
                              ...currentConfig, 
                              integrations: {...currentConfig.integrations, analytics: e.target.value}
                            })}
                            className="w-full p-3 border rounded-lg"
                          >
                            <option value="">None</option>
                            <option value="google">Google Analytics</option>
                            <option value="mixpanel">Mixpanel</option>
                            <option value="amplitude">Amplitude</option>
                            <option value="segment">Segment</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold mb-3">Business Hours</h3>
                      <label className="flex items-center gap-2 mb-3">
                        <input 
                          type="checkbox" 
                          checked={currentConfig.businessHours.enabled}
                          onChange={(e) => setCurrentConfig({
                            ...currentConfig, 
                            businessHours: {...currentConfig.businessHours, enabled: e.target.checked}
                          })}
                          className="rounded" 
                        />
                        <span className="text-sm font-medium">Enable business hours restrictions</span>
                      </label>
                      
                      {currentConfig.businessHours.enabled && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Timezone</label>
                            <select 
                              value={currentConfig.businessHours.timezone}
                              onChange={(e) => setCurrentConfig({
                                ...currentConfig, 
                                businessHours: {...currentConfig.businessHours, timezone: e.target.value}
                              })}
                              className="w-full p-3 border rounded-lg"
                            >
                              <option value="UTC">UTC</option>
                              <option value="America/New_York">Eastern Time</option>
                              <option value="America/Chicago">Central Time</option>
                              <option value="America/Denver">Mountain Time</option>
                              <option value="America/Los_Angeles">Pacific Time</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'embed' && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-semibold">Embed Code</h2>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Website Integration Code</label>
                      <div className="bg-gray-50 rounded-lg p-4 border">
                        <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                          {generateEmbedCode(currentConfig.id)}
                        </pre>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => copyEmbedCode(currentConfig.id)}
                          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
                        >
                          <Copy size={16} />
                          Copy Code
                        </button>
                        <button
                          onClick={() => {
                            const blob = new Blob([generateEmbedCode(currentConfig.id)], { type: 'text/html' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `voice-bot-${currentConfig.id}.html`;
                            a.click();
                          }}
                          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
                        >
                          <Download size={16} />
                          Download
                        </button>
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        Add this code to your website's HTML before the closing &lt;/body&gt; tag.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Voice Bot Management</h1>
          <p className="text-gray-600 mt-2">Manage voice bot configurations across all your websites</p>
        </div>
        <div className="flex gap-3 items-center">
          {/* User Profile Card */}
          <div className="bg-white border rounded-lg px-4 py-2 shadow-sm">
            <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
            <p className="text-xs text-gray-500">{user?.email}</p>
          </div>
          
          {/* Logout Button */}
          <button
            onClick={logout}
            className="bg-white border text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2 font-medium shadow-sm"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Action Buttons Row - Below Header */}
      <div className="flex justify-end gap-3 mb-6">
        <button
          onClick={() => setShowTokenUsage(true)}
          className="bg-green-600 text-white px-5 py-2.5 rounded-lg hover:bg-green-700 flex items-center gap-2 font-medium shadow-sm"
        >
          <DollarSign size={18} />
          Token Usage
        </button>
        <button
          onClick={handleCreateNew}
          className="bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium shadow-sm"
        >
          <Plus size={18} />
          Add Website
        </button>
      </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Websites</p>
                <p className="text-2xl font-bold text-gray-900">{websites.length}</p>
              </div>
              <Globe className="text-blue-600" size={24} />
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Sessions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {websites.filter(w => w.status === 'active').reduce((sum, w) => sum + w.sessions, 0)}
                </p>
              </div>
              <Users className="text-green-600" size={24} />
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Conversations</p>
                <p className="text-2xl font-bold text-gray-900">
                  {websites.reduce((sum, w) => sum + (w.totalConversations || 0), 0).toLocaleString()}
                </p>
              </div>
              <MessageSquare className="text-purple-600" size={24} />
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Satisfaction</p>
                <p className="text-2xl font-bold text-gray-900">
                  {Math.round(websites.reduce((sum, w) => sum + (w.satisfactionRate || 0), 0) / websites.length)}%
                </p>
              </div>
              <BarChart3 className="text-orange-600" size={24} />
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-sm border mb-6">
          <div className="p-6 border-b">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Configured Websites</h2>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search websites..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 w-64"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Website</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Industry</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Sessions</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Satisfaction</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Response Time</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Last Updated</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredWebsites.map((website) => (
                  <tr key={website.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{website.name}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Globe size={14} />
                          {website.domain}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{website.industry}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          website.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : website.status === 'paused'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {website.status}
                        </span>
                        <button
                          onClick={() => handleStatusToggle(website.id)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title={website.status === 'active' ? 'Pause' : 'Activate'}
                        >
                          {website.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{website.sessions.toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <span>{website.satisfactionRate}%</span>
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full" 
                            style={{ width: `${website.satisfactionRate}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{website.avgResponseTime}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{website.lastUpdated}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => window.open(`https://${website.domain}`, '_blank')}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Visit Website"
                        >
                          <ExternalLink size={16} />
                        </button>
                        <button
                          onClick={() => copyEmbedCode(website.id)}
                          className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                          title="Copy Embed Code"
                        >
                          <Copy size={16} />
                        </button>
                        <button
                          onClick={() => handleEdit(website)}
                          className="p-2 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg"
                          title="Edit Configuration"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                          title="View Analytics"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => setShowDeleteModal(website)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete Configuration"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredWebsites.length === 0 && (
              <div className="text-center py-12">
                <Globe size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No websites found</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm || filterStatus !== 'all' 
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Get started by adding your first website configuration.'
                  }
                </p>
                {!searchTerm && filterStatus === 'all' && (
                  <button
                    onClick={handleCreateNew}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                  >
                    Add Your First Website
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <DeleteModal
          website={showDeleteModal}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(null)}
        />
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3">
            <Loader2 size={24} className="animate-spin text-blue-600" />
            <span>Processing...</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Auth page wrapper
const AuthPage: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const { login } = useAuth();

  const handleAuthSuccess = (token: string, user: any) => {
    login(token, user);
  };

  if (isLogin) {
    return (
      <Login
        onLoginSuccess={handleAuthSuccess}
        onSwitchToRegister={() => setIsLogin(false)}
      />
    );
  }

  return (
    <Register
      onRegisterSuccess={handleAuthSuccess}
      onSwitchToLogin={() => setIsLogin(true)}
    />
  );
};

// Main App with routing
const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<AuthPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <VoiceBotDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;